import { useEffect, useState } from "react";
import { getHeadersForRequest, numberWithCommas } from "../utils/functions";
import axios from "axios";
import { CustomerList } from "../utils/types";
import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  CheckboxGroup,
  Collapse,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import Nav from "../components/navbar";
import { Loading } from "../components/Loading";
import { AddCustomer } from "../components/AddCustomer";
import { useNavigate } from "react-router-dom";
import useCustomTranslation from "../hooks/useCustomTranslation";
import moment from "moment";
import ReactMarkdown from "react-markdown";

const CustomerScreen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>("Loading");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<CustomerList[]>([]);
  const [customerDetails, setCustomerDetails] = useState<CustomerList[]>([]);
  const [customerBalance, setCustomerBalance] = useState<number>(0);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerList[]>([]);
  const [searchTerm, setSearchTerm] = useState<String>("");
  const [totalOwed, setTotalOwed] = useState<number>(0);
  const [count, setCount] = useState(0);
  const [paymentData, setPaymentData] = useState();
  const [transactionData, setTransactionData] = useState();
  const [prediction, setPrediction] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([1, 6]);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const { t, changeLanguage } = useCustomTranslation();

  const markdownContent = '"## Summary of Findings\n\n1. **Relationship Between Payment Dates and Order Dates:**\n   - The payments and orders are not directly aligned on a case-by-case basis, suggesting that payments might cover multiple orders or there are separate transactions that are not one-to-one matching.\n   - Payments tend to closely follow or precede the order dates but not consistently; this indicates varied payment terms or customer-specific agreements.\n\n2. **Patterns in Payment Timing:**\n   - There are visible patterns where payments occur at regular frequencies, generally following the end of an invoicing cycle.\n   - Some months such as March 2023, July 2024, and March 2025 show single or clustered payment transactions which might indicate bulk closing of accounts or end-of-month settlements.\n   - The payment amounts can vary significantly month-to-month, indicating irregular order frequencies, varied service/product pricing, or staggered payment terms.\n\n3. **Gaps and Overlaps:**\n   - There are periods with overlaps where multiple payments occur within short timeframes, which could be the settlement of backlogged invoices.\n   - Conversely, gaps exist where for significant periods no payments occur, such as in September 2023 and January 2024, which may signify no orders or delayed payments from clients.\n\n4. **Calculated Balances:**\n   - To calculate balances: `(Cumulative Orders - Cumulative Payments) for each subsequent date`.\n   - Payments generally seem to cover or slightly lag behind orders, creating floating balances that reflect a typical rolling account system.\n\n5. **Average Days Between Payments:**\n   - Utilize the paymentDate field to measure the average days between successive payments.\n   - Calculate the difference in days between each adjacent payment date and compute the mean of these values.\n\n### Estimated Next Payment Date\n\n- The historical data suggest payments are made, on average, 15 to 45 days apart.\n- The most recent payment was on 2025-03-03. Observing the pattern and average timing, the next payment is estimated to be approximately 30 days later.\n- **Estimated Date of Next Payment: 2025-04-02**\n\n### Methodology for Estimation\n\n1. **Data Preparation:**\n   - Consolidate and aggregate payments and orders by month/year to understand high-level temporal relationships.\n   - Calculate cumulative totals to understand payment coverage over order totals over time.\n\n2. **Payment Gap and Overlap Analysis:**\n   - Analyze the timing of payments versus orders to flag gaps and overlaps. Consider typical payment terms (e.g., NET30) as influencing factors for deviations.\n\n3. **Statistical Computation of Payment Intervals:**\n   - Compute the intervals between payment dates using basic time calculations (`average_days_between = (latest_payment_date - earliest_payment_date) / number_of_intervals`).\n   - Consider statistical measures like mean and median to mitigate skew from outliers and uneven payment distributions.\n\n4. **Trend Analysis for Next Payment Prediction:**\n   - Develop a rolling average of payment intervals, weigh these intervals with recent monthly trends, and project the next probable time frame for payment based on recent behavior:\n     - Evaluate recent activities (last 6 months or most recent yearly quarter) for closer estimation.\n     - Apply linear regression or similar trends analysis to project future dates mid-term.\n\nThis comprehensive approach capitalizes on behavioral patterns while being mindful of the inherent irregularities in the dataset.';

  type responseData = {
    responseData : CustomerList[]
  }

  useEffect(() => {
    setIsLoading(true);
    getAllCustomers();
    getTotalBalance();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    filterCustomers();
  }, [searchTerm, customers, selectedStatuses]);

  const getCustomerStatement = async () => {
    const headers = await getHeadersForRequest();
    console.log(customerDetails[0].id);

    try {
      // const url = `${process.env.REACT_APP_TEST_API_URL}/payments/exportStatementMultiple/${customerDetails[0].id},9`;
      const url = `${process.env.REACT_APP_TEST_API_URL}/payments/exportStatement/${customerDetails[0].id}`;
      console.log(url);

      const response = await axios.get(url, {
        headers,
        responseType: "blob", // Ensure the response is a Blob
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.setAttribute(
        "download",
        `${customerDetails[0].name}-${moment(new Date()).format(
          "DDMMMYY"
        )}-Statement.xlsx`
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setIsLoading(false);
    } catch (error) {
      console.error("Error exporting data:", error);
      setIsLoading(false);
    }
  };

  const getAllCustomers = async () => {
    const headers = await getHeadersForRequest();
    try {
      const response = await axios.get<CustomerList[]>(
        `${process.env.REACT_APP_TEST_API_URL}/payments/customerBalances`,
        { headers }
      );
      setTotalOwed(
        response.data.reduce(
          (total, customer) => total + Number(customer.balance),
          0
        )
      );
      // Sort filtered customers by balance in descending order
      const sortedFilteredData = response.data.sort(
        (a, b) => Number(b.balance) - Number(a.balance)
      );
      setCustomers(sortedFilteredData);
      setCount(1);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  // const filterCustomers = () => {
    
  //   let filteredList = customers;

  //   console.log("start",filteredList);

  //   if (showActiveCustomers) {
  //     filteredList = filteredList.filter(customer => customer.status === 1);
  //     console.log(filteredList);
  //   }

  //   if (!searchTerm) {
  //     setFilteredCustomers(filteredList);
  //     if (count === 1) {
  //       setIsLoading(false);
  //     }
  //   } else {
  //     const lowercasedFilter = searchTerm.toLowerCase();
  //     const filteredData =
  //       customers !== null
  //         ? filteredList.filter((customer) =>
  //             Object.keys(customer).some((key: any) => {
  //               const value = customer[key as keyof CustomerList];
  //               return (
  //                 value !== null && value !== undefined &&
  //                 value.toString().toLowerCase().includes(lowercasedFilter)
  //               );
  //             })
  //           )
  //         : [];
  //     // Sort filtered customers by balance in descending order
  //     const sortedFilteredData = filteredData.sort(
  //       (a, b) => Number(b.balance) - Number(a.balance)
  //     );
  //     setFilteredCustomers(sortedFilteredData);
  //     setIsLoading(false);
  //   }
  // };


  const filterCustomers = () => {
    let filteredList = customers;

    // Filter customers by the selected statuses
    if (selectedStatuses.length) {
      filteredList = filteredList.filter(customer =>
        customer.status !== undefined && selectedStatuses.includes(customer.status)
      );
    }

    // Apply the search term if provided
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filteredList = filteredList.filter(customer =>
        Object.keys(customer).some((key: any) => {
          const value = customer[key as keyof CustomerList];
          return (
            value !== null &&
            value !== undefined &&
            value.toString().toLowerCase().includes(lowercasedFilter)
          );
        })
      );
    }
    // Sort filtered customers by balance in descending order
    const sortedFilteredData = filteredList.sort(
      (a, b) => Number(b.balance) - Number(a.balance)
    );
    setFilteredCustomers(sortedFilteredData);
    setIsLoading(false);
  };

  const loadCustomerDetails = async (id: number, balance: number) => {
    setModalIsLoading(true);
    const headers = await getHeadersForRequest();
    try {
      const response = await axios.post<responseData>(
        `${process.env.REACT_APP_TEST_API_URL}/customers/getdetails`,
        { id: id },
        { headers }
      );
      console.log(response.data);
      setModalIsLoading(false);
      setCustomerDetails(response.data.responseData);
      setCustomerBalance(balance);
    } catch (error) {
      console.error(error);
      setModalIsLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    setModalIsLoading(true);
    const oldHeaders = await getHeadersForRequest();
    const newHeaders = {
      id: customerDetails[0].id,
    };
    const headers = { ...oldHeaders, ...newHeaders };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_TEST_API_URL}/analytics/customerPayments`,
        {
          headers,
        }
      ); // Fetch for customerId 12
      const data = response.data;

      const sortedData = response.data.sort((a: any, b: any) => {
        if (a["paymentDate"] > b["paymentDate"]) return -1;
        if (a["paymentDate"] < b["paymentDate"]) return 1;
        return 0;
      });

      // console.log("Payment JSON: " + JSON.stringify(sortedData));

      return sortedData;
      setPaymentData(data); // Store the fetched data
      fetchOrderHistory();
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  const fetchOrderHistory = async () => {
    const oldHeaders = await getHeadersForRequest();
    const newHeaders = {
      id: customerDetails[0].id,
    };
    const headers = { ...oldHeaders, ...newHeaders };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_TEST_API_URL}/analytics/customerTransactions`,
        {
          headers,
        }
      ); // Fetch for customerId 12
      const data = response.data;

      const sortedData = response.data.sort((a: any, b: any) => {
        if (a["invoiceDate"] > b["invoiceDate"]) return -1;
        if (a["invoiceDate"] < b["invoiceDate"]) return 1;
        return 0;
      });

      // console.log("Transaction JSON: " + JSON.stringify(sortedData));

      return sortedData;
      // setTransactionData(data); // Store the fetched data
      // askGPT();
      return data;
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  const askGPT = async () => {
    // setModalIsLoading(true);
    const newPaymentData = {
      paymentData: await fetchPaymentHistory(),
    };

    const newTransactionData = {
      transactionData: await fetchOrderHistory(),
    };
    const body = { ...newPaymentData, ...newTransactionData };

    // console.log(body);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_TEST_API_URL}/analytics/askGPT`,
        {
          body,
        }
      );
      const data = response.data;
      setModalIsLoading(false);
      setPrediction(data);
    } catch (error) {
      setModalIsLoading(false);
      console.error(error);
    }
  };

  // Function to calculate the total balance
  const getTotalBalance = () => {
    const total = customers.reduce(
      (total, customer) => total + Number(customer.balance),
      0
    );
    console.log(total);
  };

  const navigateToPresetPayments = (customerId: number, customerName: string) => {
    navigate(`/CustomerPresetPrices/${customerId}/${customerName}`);
  };

  const navigateToOrders = (customerId: number, customerName: string) => {
    navigate(`/CustomerOrders/${customerId}/${customerName}`);
  };

  const navigateToPayments = (customerId: number, customerName: string) => {
    navigate(`/CustomerPayments/${customerId}/${customerName}`);
  };

  const navigateToAnalytics = (customerId: number, customerName: string) => {
    navigate(`/CustomerAnalytics/${customerId}/${customerName}`);
  };

  const navigateToEdit = (customer: CustomerList) => {
    console.log("customer got on navigate button", customer);
    const data = {customer: customer, isEdit: true}
    const encoded = encodeURIComponent(JSON.stringify(data));
    navigate(`/Customer/${encoded}`);
  }

  const navigateToAdd = () => {
    const data = {customer: [], isEdit: false};
    const encoded = encodeURIComponent(JSON.stringify(data));
    navigate(`/Customer/${encoded}`);
  }

  return (
    <Stack>
      <Stack>
        <Stack position="sticky" top={0} zIndex={1} width="full">
          <Nav pageName={"Customers"} />
        </Stack>
        <Stack px={isSmallScreen ? 2 : 10 } py={10}>
          {isLoading ? (
            <Center h={600}>
              <Loading />
            </Center>
          ) : (
            <Stack >
              <HStack
                alignItems={"center"}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
              >
                <InputGroup maxW={500}>
                  <Input
                    placeholder={t("Search for a customer...")}
                    width="100%"
                    borderRadius="4"
                    py="3"
                    px="1"
                    fontSize="14"
                    onChange={(text) => setSearchTerm(text.target.value)}
                    autoCapitalize="none"
                  />
                  <InputRightAddon>{t("Search")}</InputRightAddon>
                </InputGroup>

                <Text  alignSelf={"center"} fontSize={20} textAlign={"center"}>
                  {t("Total recievable:")} {numberWithCommas(totalOwed)}/=
                </Text>

                <Button alignSelf={"center"} 
                  onClick={() => {
                    navigateToAdd();
                    // setIsEditMode(false);
                    // setIsAddMode(true);
                  }}
                >
                  {t("Add Customer")}
                </Button>

                {isSmallScreen && (<Button alignSelf={"center"} onClick={()=>{navigate(`/CustomerMap`)}}>See Map</Button>)}
              </HStack>

              <CheckboxGroup
                colorScheme="blue"
                value={selectedStatuses.map(String)}
                onChange={(values) => setSelectedStatuses(values.map(Number))}
              >
                {isSmallScreen && (<Text alignSelf={"center"}>Filters: </Text>)}
                <HStack spacing={5} my={4} wrap="wrap" justifyContent={isSmallScreen ? "center" : "unset"}>
                  {!isSmallScreen && (<Text>Filters: </Text>)}
                  <Checkbox value="1">{t("Active")}</Checkbox>
                  <Checkbox value="2">{t("Approachable")}</Checkbox>
                  <Checkbox value="3">{t("Appraoched")}</Checkbox>
                  <Checkbox value="4">{t("Rejected")}</Checkbox>
                  <Checkbox value="5">{t("Deleted")}</Checkbox>
                  <Checkbox value="6">{t("Individuals")}</Checkbox>
                </HStack>
              </CheckboxGroup>

              
              {!isSmallScreen && (<Button alignSelf={"center"} onClick={()=>{navigate(`/CustomerMap`)}}>See Map</Button>)}

              {/* <Stack h={10} /> */}

              {filteredCustomers.length > 0 ? (
                <TableContainer>
                  <Table size={isSmallScreen ? "sm" : "md"} variant="striped">
                    <TableCaption>{t("List of customers")}</TableCaption>
                    <Thead>
                      <Tr>
                        {!isSmallScreen && (<Th>ID</Th>)}
                        <Th>{t("Customer Name")}</Th>
                        {/* <Th>Location</Th> */}
                        {!isSmallScreen && (<Th>{t("TIN")}</Th>)}
                        <Th isNumeric>{t("Balance")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredCustomers.map((customer: any, index: number) => (
                        <Tr
                          onClick={() => {
                            setShowDetailsModal(true);
                            const id = parseInt(customer.customerId);
                            loadCustomerDetails(id, customer.balance);
                          }}
                        >
                          {!isSmallScreen && (<Td>{index + 1} </Td>)}
                          <Td>{customer.customerName}</Td>
                          {/* <Td
                            // onClick={() => {
                            //   const url = `https://www.google.com/maps/dir/?api=1&destination=${customer.latitude},${customer.longitude}`;
                            //   window.open(url, "_blank");
                            // }}
                          >
                            {customer.location}
                          </Td> */}
                          {!isSmallScreen && (<Td>{customer.customerTIN}</Td>)}
                          <Td isNumeric>
                            {numberWithCommas(customer.balance)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                    <Tfoot>
                      <Tr>
                        {!isSmallScreen && (<Th>ID</Th>)}
                        <Th>{t("Customer Name")}</Th>
                        {/* <Th>Location</Th> */}
                        {!isSmallScreen && (<Th>{t("TIN")}</Th>)}
                        <Th isNumeric>{t("Balance")}</Th>
                      </Tr>
                    </Tfoot>
                  </Table>
                </TableContainer>
              ) : (
                <Stack alignItems={"center"} justifyContent={"center"} h={400}>
                  <Text
                    fontSize={20}
                    textAlign={"center"}
                    // color={primary}
                  >
                    {t("There are no Customers")}
                  </Text>
                  <Text
                    fontSize={20}
                    textAlign={"center"}
                    // color={primary}
                  >
                    Click the + button to add one!
                  </Text>
                </Stack>
              )}
            </Stack>
          )}

          {/* CUSTOMER DETAILS MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setPrediction("");
            }}
          >
            <ModalOverlay
              bg="blackAlpha.300"
              backdropFilter="blur(10px) hue-rotate(90deg)"
            />
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>{t("Customer Details")}</ModalHeader>
              <ModalBody>
                {modalIsLoading ? (
                  <Loading />
                ) : (
                  customerDetails.length > 0 && (
                    <>
                      <Text>
                        {t("Name:")} {customerDetails[0].name}
                      </Text>
                      <Text>
                        {t("ID Number:")} {customerDetails[0].tin}
                      </Text>
                      <Text>
                        {t("Phone Number:")} {customerDetails[0].phoneNumber}
                      </Text>
                      <Text>
                        {t("ID Type:")} {customerDetails[0].category}
                      </Text>
                      <Text>Balance: {numberWithCommas(customerBalance)}</Text>
                      <Text>
                        Turnover Ratio:{" "}
                        {numberWithCommas(
                          customerDetails[0].accounts_receivable_turnover
                            .turnover_ratio
                        )}{" "}
                        ({" "}
                        {numberWithCommas(
                          365 /
                            customerDetails[0].accounts_receivable_turnover
                              .turnover_ratio
                        )}{" "}
                        days )
                      </Text>

                      <br />

                      <HStack justifyContent={"space-around"}>
                        <Button
                          onClick={() => getCustomerStatement()}
                          // bg={primary}
                          width={"100"}
                          flex={1}
                        >
                          {t("Statement")}
                        </Button>
                        <Button
                          onClick={() =>
                            navigateToAnalytics(
                              customerDetails[0].id,
                              customerDetails[0].name
                            )
                          }
                          // bg={primary}
                          width={"100"}
                          flex={1}
                        >
                          {t("Analytics")}
                        </Button>
                      </HStack>

                      <br />

                      <HStack justifyContent={"space-around"}>
                        <Button
                          onClick={() =>
                            navigateToPayments(
                              customerDetails[0].id,
                              customerDetails[0].name
                            )
                          }
                          // bg={primary}
                          width={"100"}
                          flex={1}
                        >
                          {t("Payments")}
                        </Button>
                        <Button
                          onClick={() =>
                            navigateToOrders(
                              customerDetails[0].id,
                              customerDetails[0].name
                            )
                          }
                          width={"100"}
                          flex={1}
                        >
                          {t("Orders")}
                        </Button>
                      </HStack>

                      {customerDetails[0].latitude !== null && (
                        <>
                          <br />
                          <Stack>
                            <Button
                              onClick={() => {
                                const url = `https://www.google.com/maps/dir/?api=1&destination=${customerDetails[0].latitude},${customerDetails[0].longitude}`;
                                window.open(url, "_blank");
                              }}
                            >
                              Get Directions
                            </Button>
                          </Stack>
                        </>
                      )}
                      
                      <br />
                      
                      <Stack>
                        <Button onClick={() => navigateToPresetPayments(
                              customerDetails[0].id,
                              customerDetails[0].name
                            )}>
                          Preset Prices
                        </Button>
                      </Stack>

                      {customerBalance > 0 && (
                        <>
                          <br />
                          <Stack>
                            <Button onClick={() => askGPT()}>
                              Predict Next Payment
                            </Button>
                          </Stack>
                        </>
                      )}
                      
                      {/* <ReactMarkdown>{markdownContent}</ReactMarkdown> */}

                      {prediction !== "" && (
                        <>
                          <Text>Next Payment is expected to be on:</Text>
                          <Text>
                            {moment(prediction).format("DD MMM YYYY")}
                          </Text>

                          <Collapse>
                            {/* <ReactMarkdown>{markdownContent}</ReactMarkdown> */}
                          </Collapse>
                        </>
                      )}
                    </>
                  )
                )}
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowDetailsModal(false);
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    disabled={isLoading}
                    // bg={primary}
                    onClick={() => {
                      navigateToEdit(customerDetails[0]);
                    }}
                  >
                    {t("Edit")}
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* ADD CUSTOMER MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={isAddMode}
            onClose={() => {
              setIsAddMode(false);
              getAllCustomers();
            }}
          >
            <ModalOverlay
              bg="blackAlpha.300"
              backdropFilter="blur(10px) hue-rotate(90deg)"
            />
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>{t("Add Customer")}</ModalHeader>
              <ModalBody>
                {modalIsLoading ? (
                  <Loading />
                ) : (
                  <Stack flex={1}>
                    <Stack alignItems={"center"}>
                      <Stack>
                        <AddCustomer
                          name={""}
                          id={""}
                          location={""}
                          phoneNumber={""}
                          tin={""}
                          isEdit={isEditMode}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* EDIT CUSTOMER MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={isEditMode}
            onClose={() => {
              setIsEditMode(false);
              getAllCustomers();
            }}
          >
            <ModalOverlay
              bg="blackAlpha.300"
              backdropFilter="blur(10px) hue-rotate(90deg)"
            />
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>{t("Customer Details")}</ModalHeader>
              <ModalBody>
                {modalIsLoading ? (
                  <Loading />
                ) : (
                  isEditMode && (
                    <Stack flex={1}>
                      <Stack alignItems={"center"}>
                        <Stack>
                          <AddCustomer
                            name={
                              customerDetails ? customerDetails[0].name : ""
                            }
                            id={
                              customerDetails
                                ? customerDetails[0].id.toString()
                                : ""
                            }
                            location={
                              customerDetails ? customerDetails[0].location : ""
                            }
                            phoneNumber={
                              customerDetails
                                ? customerDetails[0].phoneNumber
                                : ""
                            }
                            tin={customerDetails ? customerDetails[0].tin : ""}
                            isEdit={isEditMode}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  )
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CustomerScreen;
